import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { formatDate } from '../../utilities.js';
import { useAuth } from '../../context/AuthContext.js';
import '../student/AllStudents.css';
import LocationFilter from '../locaties/LocationFilter.js';
import LifecycleActionButtons from '../../components/LifecycleActionButtons.js';
import {
  confirmLifecycleAction,
  showLifecycleSuccess,
  showLifecycleError
} from '../../utils/confirmLifecycleAction.js';
import { getStatusBadgeInfo } from '../../utils/lifecycleUtils.js';

function AllStudents() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');
  const { userInfo, isAuthenticated } = useAuth();
  const userRole = userInfo?.role || '';
  const is_single_user = userInfo?.is_single_user;

  const [students, setStudents] = useState([]);
  const [zoekterm, setZoekterm] = useState('');
  const [debouncedZoekterm, setDebouncedZoekterm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState('all');

  const [showUnenrolled, setShowUnenrolled] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false
  });

  axios.defaults.withCredentials = true;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedZoekterm(zoekterm.trim());
    }, 400);

    return () => clearTimeout(timer);
  }, [zoekterm]);

  const fetchStudents = useCallback(() => {
    setIsLoading(true);

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/students/allstudents`, {
        params: {
          page,
          limit,
          search: debouncedZoekterm,
          location_id: locationFilter,
          showUnenrolled,
          showArchived
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      .then((response) => {
        setStudents(response.data.Data || []);
        setPagination(
          response.data.pagination || {
            page: 1,
            limit,
            total: 0,
            totalPages: 1,
            hasNextPage: false,
            hasPrevPage: false
          }
        );
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching students', error);
        setStudents([]);
        setPagination({
          page: 1,
          limit,
          total: 0,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false
        });
        setIsLoading(false);
      });
  }, [token, page, limit, debouncedZoekterm, locationFilter, showUnenrolled, showArchived]);

  const getLocationName = (id) => {
    if (!id) return 'nvt';
    const loc = locations.find((l) => l.location_id === id);
    return loc ? loc.location_name : id;
  };

  useEffect(() => {
    if (userRole === 'super_admin' || userRole === 'locatie_admin') {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/location/allLocations`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        .then((res) => {
          const locs = res.data.data || res.data;
          setLocations(Array.isArray(locs) ? locs : []);
        })
        .catch((err) => console.error('Error fetching locations', err));
    }
  }, [userRole, token]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleViewStudent = (id) => navigate(`/viewstudent/${id}`);

  const handleLifecycleAction = async (studentId, action, entityLabel) => {
    const { isConfirmed } = await confirmLifecycleAction(action, entityLabel);
    if (!isConfirmed) return;

    setActionLoadingId(studentId);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/students/${studentId}/${action}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      await showLifecycleSuccess(
        action,
        entityLabel,
        res.data?.message || ''
      );

      fetchStudents();
    } catch (error) {
      console.error(`Error during lifecycle action ${action}`, error);
      await showLifecycleError(
        error?.response?.data?.message || `Actie mislukt: ${action}`
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const getStatusBadge = (student) => {
    const badge = getStatusBadgeInfo(student);
    return <span className={badge.className}>{badge.label}</span>;
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const renderPaginationButtons = () => {
    if ((pagination.totalPages || 1) <= 1) return null;

    const currentPage = pagination.page || 1;
    const totalPages = pagination.totalPages || 1;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(totalPages, 5);
    }

    if (currentPage >= totalPages - 1) {
      startPage = Math.max(1, totalPages - 4);
    }

    const buttons = [];

    for (let i = startPage; i <= endPage; i += 1) {
      buttons.push(
        <button
          key={i}
          type="button"
          className={`btn btn-sm ${i === currentPage ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => setPage(i)}
        >
          {i}
        </button>
      );
    }

    return buttons;
  };

  const startRecord = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endRecord =
    pagination.total === 0
      ? 0
      : Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <div className="container-fluid px-3 px-md-5 mt-4">
      {isAuthenticated ? (
        <>
          <div className="row mb-4 align-items-center">
            <div className="col-12 col-md-6 text-center text-md-start mb-2 mb-md-0">
              <h2 className="h4">Overzicht Van Alle Leerlingen</h2>
            </div>
            <div className="col-12 col-md-6 text-end text-md-center">
              <Link to="/newstudent" className="btn btn-success">
                <i className="bi bi-plus-circle me-2"></i>Nieuwe leerling
              </Link>
            </div>
          </div>

          {!is_single_user && (
            <div className="mb-3">
              <LocationFilter
                userRole={userRole}
                locationFilter={locationFilter}
                setLocationFilter={(value) => {
                  setLocationFilter(value);
                  setPage(1);
                }}
                locations={locations}
              />
            </div>
          )}

          <div className="row mb-3">
            <div className="col-12 col-md-4 mb-2">
              <input
                type="text"
                className="form-control rounded-0"
                placeholder="Zoek op voornaam of achternaam"
                value={zoekterm}
                onChange={(e) => {
                  setZoekterm(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className="col-12 col-md-2 mb-2">
              <select
                className="form-select rounded-0"
                value={limit}
                onChange={handleLimitChange}
              >
                <option value={10}>Toon 10</option>
                <option value={20}>Toon 20</option>
                <option value={50}>Toon 50</option>
                <option value={100}>Toon 100</option>
              </select>
            </div>

            <div className="col-12 col-md-6 d-flex flex-column flex-md-row gap-3 align-items-md-center">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showUnenrolled"
                  checked={showUnenrolled}
                  onChange={(e) => {
                    setShowUnenrolled(e.target.checked);
                    setPage(1);
                  }}
                />
                <label className="form-check-label" htmlFor="showUnenrolled">
                  Toon uitgeschreven leerlingen
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showArchived"
                  checked={showArchived}
                  onChange={(e) => {
                    setShowArchived(e.target.checked);
                    setPage(1);
                  }}
                />
                <label className="form-check-label" htmlFor="showArchived">
                  Toon gearchiveerde leerlingen
                </label>
              </div>

              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setZoekterm('');
                  setLocationFilter('all');
                  setShowUnenrolled(false);
                  setShowArchived(false);
                  setLimit(20);
                  setPage(1);
                }}
              >
                Filters wissen
              </button>
            </div>
          </div>

          <div className="row mb-3 align-items-center">
            <div className="col-12">
              <small className="text-muted">
                Toont {startRecord} t/m {endRecord} van {pagination.total || 0} leerlingen
              </small>
            </div>
          </div>

          {isLoading ? (
            <p>Loading...</p>
          ) : (
            <>
              <div className="row">
                {students.map((student) => (
                  <div key={student.student_id} className="col-12 col-md-6 col-lg-4 mb-4">
                    <div
                      className="card h-100 shadow-sm"
                      onClick={() => handleViewStudent(student.student_id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="card-body d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h5 className="card-title mb-0">
                            {student.firstname} {student.lastname}
                          </h5>
                          {getStatusBadge(student)}
                        </div>

                        <ul className="list-unstyled small">
                          <li><strong>Leerlingnr:</strong> {student.customer_student_number || '-'}</li>
                          <li><strong>Locatie:</strong> {getLocationName(student.location_id)}</li>
                          <li><strong>Datum in:</strong> {formatDate(student.datein)}</li>
                          <li><strong>Datum uit:</strong> {formatDate(student.dateout)}</li>
                          {student.email && <li><strong>E-mail:</strong> {student.email}</li>}
                        </ul>

                        <div
                          className="mt-auto d-flex flex-wrap justify-content-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <LifecycleActionButtons
                            item={student}
                            itemId={student.student_id}
                            entityLabel="leerling"
                            editPath={`/editstudent/${student.student_id}`}
                            loadingId={actionLoadingId}
                            onAction={handleLifecycleAction}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {students.length === 0 && (
                  <div className="col-12 text-center text-muted">
                    Geen leerlingen gevonden.
                  </div>
                )}
              </div>

              {(pagination.totalPages || 1) > 1 && (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mt-3">
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={!pagination.hasPrevPage}
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    >
                      Vorige
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={!pagination.hasNextPage}
                      onClick={() => setPage((prev) => prev + 1)}
                    >
                      Volgende
                    </button>
                  </div>

                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    {renderPaginationButtons()}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <p>Je bent niet geauthenticeerd.</p>
      )}
    </div>
  );
}

export default AllStudents;